import { Fragment, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Img,
  Input,
  Select,
  SimpleGrid,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import ReactPaginate from "react-paginate";
import chevronLeft from "../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../assets/images/employee/chevron-right.svg";
import useDebounce from "../../hooks/useDebounce";
import { useCheckinDebugLogs } from "../../hooks/useCheckinDebugLog";

/**
 * LOG TẠM 16/10: trang ẩn /checkin-debug-log (không có trên menu, chỉ quản trị).
 * Xem log chấm vào / chấm ra theo ngày để tìm lý do NV không chấm được. Gỡ cùng log tạm.
 */

const STEP_LABEL = {
  eligibility: "Mở màn chấm công",
  startCheckin: "Mở màn chấm vào",
  verifyCheckinPlace: "Kiểm tra địa điểm",
  verifyBssid: "Kiểm tra wifi",
  verifyQrCode: "Quét QR",
  verifyImage: "Gửi ảnh chấm vào",
  completeCheckin: "Hoàn thành chấm vào",
  verify: "Kiểm tra trước chấm ra",
  image: "Đóng dấu ảnh chấm ra",
  checkout: "Chấm ra",
};

const ERROR_LABEL = {
  OUT_OF_RANGE: "Ngoài phạm vi",
  DEVICE_NOT_REGISTERED: "Sai thiết bị",
  NO_OPEN_CHECKIN: "Không có lượt cần chấm ra",
  CHECKIN_NOT_REGISTERED: "Chưa đăng ký thiết bị",
  IMAGE_REQUIRED: "Thiếu ảnh",
  ALREADY_CHECKED_OUT: "Đã chấm ra rồi",
  ABORTED: "Mất kết nối giữa chừng",
  ERROR: "Lỗi hệ thống",
  PLACE_INACTIVE: "Địa điểm ngưng dùng",
  CHECKOUT_NOT_ENABLED: "Chưa bật luồng mới",
};

const TAG_STYLE = {
  CHECKIN: { label: "Chấm vào", scheme: "blue" },
  CHECKOUT: { label: "Chấm ra", scheme: "purple" },
  ELIG: { label: "Cần chấm ra", scheme: "orange" },
};

const cardProps = {
  backgroundColor: "var(--color-main)",
  borderRadius: "12px",
  p: "16px",
};

const Stat = ({ label, value, color, hint }) => (
  <Box {...cardProps}>
    <Text fontSize="13px" color="#51607B">
      {label}
    </Text>
    <Text fontSize="26px" fontWeight="700" color={color || "var(--color-info-employee)"}>
      {value ?? "-"}
    </Text>
    {hint && (
      <Text fontSize="12px" color="#8C97AB">
        {hint}
      </Text>
    )}
  </Box>
);

const StatusBadge = ({ row }) => {
  if (row.tag === "ELIG") return <Text color="#8C97AB">-</Text>;
  if (row.status === "ABORTED") return <Badge colorScheme="red">Huỷ</Badge>;
  return <Badge colorScheme={row.isError ? "red" : "green"}>{row.status}</Badge>;
};

const EmployeeList = ({ title, note, rows, columns, onPick }) => (
  <Box {...cardProps}>
    <Text fontWeight="700" color="var(--color-info-employee)">
      {title} ({rows?.length || 0})
    </Text>
    {note && (
      <Text fontSize="12px" color="#8C97AB" mb="8px">
        {note}
      </Text>
    )}
    {rows?.length ? (
      <Box maxH="240px" overflowY="auto">
        <Table size="sm" variant="simple">
          <Thead position="sticky" top="0" bg="var(--color-main)">
            <Tr>
              <Th>Nhân viên</Th>
              {columns.map((c) => (
                <Th key={c.key}>{c.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr
                key={r.code || r.name}
                cursor="pointer"
                _hover={{ bg: "var(--fill-avatar)" }}
                onClick={() => onPick(r.code)}
                title="Bấm để lọc log của NV này"
              >
                <Td>
                  <Text fontWeight="600">{r.name || "-"}</Text>
                  <Text fontSize="12px" color="#8C97AB">
                    {r.code}
                  </Text>
                </Td>
                {columns.map((c) => (
                  <Td key={c.key} whiteSpace="normal" fontSize="13px">
                    {c.render ? c.render(r) : r[c.key]}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    ) : (
      <Text fontSize="13px" color="#8C97AB" mt="8px">
        Không có
      </Text>
    )}
  </Box>
);

const DetailRow = ({ row }) => {
  const d = row.detail || {};
  const items = [
    ["Thời gian xử lý", d.ms !== undefined ? `${d.ms} ms` : null],
    ["Dữ liệu app gửi lên", d.body && Object.keys(d.body).length ? JSON.stringify(d.body) : null],
    ["Kết quả chi tiết", d.result ? JSON.stringify(d.result) : null],
    ["Lượt chấm hôm nay", d.today ? JSON.stringify(d.today) : null],
    [
      "Thiết bị lúc đăng nhập",
      d.appLogin && typeof d.appLogin === "object"
        ? [d.appLogin.app, d.appLogin.os, d.appLogin.model, d.appLogin.loginAt && `đăng nhập ${d.appLogin.loginAt}`]
            .filter(Boolean)
            .join(" · ")
        : d.appLogin,
    ],
    ["User-agent / IP", [d.ua, d.ip].filter(Boolean).join("  ·  ") || null],
  ].filter(([, v]) => v);
  return (
    <Box p="12px 16px" bg="#F9FAFD" fontSize="13px">
      {items.map(([k, v]) => (
        <Flex key={k} gap="12px" mb="4px">
          <Text minW="170px" color="#51607B" fontWeight="600">
            {k}
          </Text>
          <Text wordBreak="break-all" fontFamily={v.startsWith("{") || v.startsWith("[") ? "monospace" : undefined}>
            {v}
          </Text>
        </Flex>
      ))}
    </Box>
  );
};

export default function CheckinDebugLog() {
  const [date, setDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [tag, setTag] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [hideElig, setHideElig] = useState(false);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const search = useDebounce(searchText, 400);

  useEffect(() => {
    setPage(1);
    setOpenId(null);
  }, [date, search, tag, errorsOnly, hideElig]);

  const {
    items,
    summary,
    dates,
    date: shownDate,
    total,
    totalPages,
    startEntry,
    endEntry,
    refetch,
    isLoading,
    isFetching,
    error,
  } = useCheckinDebugLogs({ date, search, tag, errorsOnly, hideElig, page });

  const pickEmployee = (code) => {
    setSearchText(code || "");
    setHideElig(false);
    setErrorsOnly(false);
    setTag("");
  };

  return (
    <Box w="100%" backgroundColor="var(--color-backgroundmain)" p="24px 16px 16px">
      <Flex justifyContent="space-between" alignItems="center" mb="16px" wrap="wrap" gap="8px">
        <Box>
          <Text fontSize="20px" fontWeight="700" color="var(--color-info-employee)">
            Log chấm vào / chấm ra
          </Text>
          <Text fontSize="13px" color="#8C97AB">
            Trang ẩn, chỉ quản trị · ghi từ 16/10/2026 · bấm 1 dòng để xem chi tiết
          </Text>
        </Box>
        <Button size="sm" onClick={() => refetch()} isLoading={isFetching}>
          Tải lại
        </Button>
      </Flex>

      <Flex {...cardProps} gap="12px" wrap="wrap" alignItems="center" mb="16px">
        <Select
          w="170px"
          value={date || shownDate || ""}
          onChange={(e) => setDate(e.target.value)}
          bg="var(--fill-avatar)"
        >
          {!dates.length && <option value="">Chưa có log</option>}
          {dates.map((d) => (
            <option key={d} value={d}>
              {d.split("-").reverse().join("/")}
            </option>
          ))}
        </Select>
        <Input
          w="300px"
          placeholder="Mã NV / tên NV (không cần dấu)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          bg="var(--fill-avatar)"
        />
        <Select w="190px" value={tag} onChange={(e) => setTag(e.target.value)} bg="var(--fill-avatar)">
          <option value="">Tất cả loại</option>
          <option value="CHECKIN">Chấm vào</option>
          <option value="CHECKOUT">Chấm ra</option>
          <option value="ELIG">Cần chấm ra</option>
        </Select>
        <Checkbox isChecked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)}>
          Chỉ lỗi
        </Checkbox>
        <Checkbox isChecked={hideElig} onChange={(e) => setHideElig(e.target.checked)}>
          Ẩn dòng "cần chấm ra"
        </Checkbox>
        {searchText && (
          <Button size="sm" variant="ghost" onClick={() => setSearchText("")}>
            Bỏ lọc NV
          </Button>
        )}
      </Flex>

      {error && (
        <Box {...cardProps} mb="16px" color="red.500">
          {error?.response?.status === 403
            ? "Bạn không có quyền xem trang này."
            : error?.response?.data?.message || error?.message || "Không tải được log"}
        </Box>
      )}

      <Text fontSize="12px" color="#8C97AB" mb="6px">
        Số liệu tổng của cả ngày (không theo bộ lọc bên trên)
      </Text>
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing="12px" mb="16px">
        <Stat label="Request chấm vào / ra" value={summary?.requests} />
        <Stat label="Lỗi" value={summary?.errors} color={summary?.errors ? "#E53E3E" : undefined} />
        <Stat label="Nhân viên có log" value={summary?.employees} />
        <Stat
          label="Không gọi chấm ra lần nào"
          value={summary?.noCheckoutCall?.length}
          color={summary?.noCheckoutCall?.length ? "#DD6B20" : undefined}
          hint="App cũ, hoặc bị chặn trên máy"
        />
        <Stat
          label="Chấm ra chưa thành công"
          value={summary?.checkoutFailed?.length}
          color={summary?.checkoutFailed?.length ? "#E53E3E" : undefined}
        />
      </SimpleGrid>

      {summary?.errorCodes?.length > 0 && (
        <Flex {...cardProps} mb="16px" gap="8px" wrap="wrap" alignItems="center">
          <Text fontWeight="600" mr="4px">
            Các lỗi trong ngày:
          </Text>
          {summary.errorCodes.map((e) => (
            <Badge key={e.code} colorScheme="red" variant="subtle" px="8px" py="2px" textTransform="none">
              {ERROR_LABEL[e.code] || e.code} × {e.count}
            </Badge>
          ))}
        </Flex>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="12px" mb="16px">
        <EmployeeList
          title="Không gọi API chấm ra lần nào"
          note="Server báo cần chấm ra nhưng app không gửi yêu cầu: app cũ (bản 2.3.x không có nút Chấm ra), hoặc app mới bị chặn ngay trên máy (GPS báo ngoài phạm vi, chưa có GPS / quyền vị trí). Xem cột App để phân biệt."
          rows={summary?.noCheckoutCall}
          onPick={pickEmployee}
          columns={[
            { key: "checkedInAt", label: "Chấm vào" },
            { key: "elig", label: "Số lần mở app", render: (r) => r.elig },
            { key: "app", label: "App (lúc đăng nhập)" },
          ]}
        />
        <EmployeeList
          title="Có bấm chấm ra nhưng chưa thành công"
          rows={summary?.checkoutFailed}
          onPick={pickEmployee}
          columns={[
            { key: "checkoutCalls", label: "Số lần gọi" },
            {
              key: "lastError",
              label: "Lỗi gần nhất",
              render: (r) => (
                <>
                  <Text fontWeight="600" color="#E53E3E">
                    {ERROR_LABEL[r.lastErrorCode] || r.lastErrorCode}
                  </Text>
                  <Text>{r.lastError}</Text>
                </>
              ),
            },
            { key: "app", label: "App" },
          ]}
        />
      </SimpleGrid>

      <Box {...cardProps} p="0">
        <Skeleton isLoaded={!isLoading}>
          <TableContainer maxH="calc(100vh - 260px)" overflowY="auto">
            <Table variant="unstyled" size="sm">
              <Thead position="sticky" top="0" zIndex="1" bg="#F9FAFD">
                <Tr>
                  {["Giờ", "Loại", "Bước", "Nhân viên", "KQ", "Lý do / kết quả", "App (lúc đăng nhập)"].map((h) => (
                    <Th key={h} p="12px" color="#51607B" textTransform="none" fontSize="13px">
                      {h}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {items.length ? (
                  items.map((r, i) => {
                    const t = TAG_STYLE[r.tag] || { label: r.tag, scheme: "gray" };
                    const open = openId === r.id;
                    return (
                      <Fragment key={r.id}>
                        <Tr
                          cursor="pointer"
                          onClick={() => setOpenId(open ? null : r.id)}
                          bg={r.isError ? "#FFF5F5" : i % 2 ? "var(--fill-avatar)" : "var(--color-main)"}
                          _hover={{ filter: "brightness(97%)" }}
                        >
                          <Td p="10px 12px" fontFamily="monospace">
                            {r.time}
                          </Td>
                          <Td p="10px 12px">
                            <Badge colorScheme={t.scheme} textTransform="none">
                              {t.label}
                            </Badge>
                          </Td>
                          <Td p="10px 12px" whiteSpace="normal">
                            <Text>{STEP_LABEL[r.step] || r.step}</Text>
                          </Td>
                          <Td p="10px 12px" whiteSpace="normal">
                            <Text fontWeight="600">{r.name || "-"}</Text>
                            <Text fontSize="12px" color="#8C97AB">
                              {r.code}
                            </Text>
                          </Td>
                          <Td p="10px 12px">
                            <StatusBadge row={r} />
                          </Td>
                          <Td p="10px 12px" whiteSpace="normal" maxW="460px">
                            {r.errorCode && r.errorCode !== "OK" && (
                              <Text fontWeight="600" color={r.isError ? "#E53E3E" : undefined}>
                                {ERROR_LABEL[r.errorCode] || r.errorCode}
                              </Text>
                            )}
                            <Text fontSize="13px">{r.reason || (r.isError ? "" : "Thành công")}</Text>
                          </Td>
                          <Td p="10px 12px" whiteSpace="normal" fontSize="13px">
                            {r.app}
                          </Td>
                        </Tr>
                        {open && (
                          <Tr>
                            <Td colSpan={7} p="0">
                              <DetailRow row={r} />
                            </Td>
                          </Tr>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={7} textAlign="center" p="40px 0">
                      Không có dữ liệu
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Skeleton>
        <Flex alignItems="center" justifyContent="space-between" p="16px 20px">
          <Text>
            {startEntry}–{endEntry} / {total} dòng
          </Text>
          <HStack spacing={2}>
            <ReactPaginate
              className="pagination-custom"
              pageCount={totalPages || 0}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={(p) => setPage(p.selected + 1)}
              containerClassName={"pagination"}
              activeClassName={"active"}
              breakClassName={"ellipsis"}
              breakLabel={"..."}
              previousLabel={<Img w="16px" minW="16px" h="16px" src={chevronLeft} />}
              nextLabel={<Img w="16px" minW="16px" h="16px" src={chevronRight} />}
              renderOnZeroPageCount={null}
              forcePage={page - 1}
            />
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
}
